<!-- Маркетинговый лейаут - Template -->
<template>
  <div class="min-h-screen bg-background text-foreground">
    <!-- Заголовок -->
    <AppHeader
      :cta-text="headerCTA"
      @cta-click="handleHeaderCTA"
    />

    <!-- Основной контент -->
    <main class="pt-16">
      <slot />
    </main>

    <!-- Подвал -->
    <AppFooter
      :tagline="footerTagline"
      :description="footerDescription"
      :navigation-items="footerNavigation"
      :contact-info="contactInfo"
      :copyright="copyright"
      :design-credit="designCredit"
    />
  </div>
</template>

<script setup lang="ts">
import AppHeader from '@/components/organisms/AppHeader.vue'
import AppFooter from '@/components/organisms/AppFooter.vue'
import type { NavItem, ContactInfo } from '@/lib/types'

interface Props {
  /** Текст CTA в заголовке */
  headerCTA?: string
  /** Слоган в футере */
  footerTagline?: string
  /** Описание в футере */
  footerDescription?: string
  /** Навигация в футере */
  footerNavigation?: NavItem[]
  /** Контактная информация */
  contactInfo?: ContactInfo
  /** Копирайт */
  copyright?: string
  /** Информация о дизайне */
  designCredit?: string
}

withDefaults(defineProps<Props>(), {
  headerCTA: 'Начать карьеру',
  footerTagline: 'Оставь свой отпечаток в музыкальной индустрии',
  footerDescription: 'Мультифункциональное приложение для артистов в Telegram. Загружай треки, получай роялти, развивай карьеру.',
  footerNavigation: () => [
    { label: 'Возможности', href: '#features-section' },
    { label: 'Приложение', href: '#app' },
    { label: 'Кейсы', href: '#cases' },
    { label: 'Тарифы', href: '#pricing' },
    { label: 'FAQ', href: '#faq' }
  ],
  contactInfo: () => ({
    email: 'hello@touch.com',
    socials: [
      { name: 'Telegram', href: 'https://t.me/touch_music', icon: '/images/telegram-icon.png' },
      { name: 'Instagram', href: 'https://instagram.com/touch.music', icon: '/images/instagram-icon.png' },
      { name: 'VK', href: 'https://vk.com/touch_music', icon: '/images/vk-icon.png' },
      { name: 'YouTube', href: 'https://youtube.com/@touch_music', icon: '/images/youtube-icon.png' }
    ]
  }),
  copyright: '© 2025 TOUCH distribution',
  designCredit: 'design by ilya soderolov'
})

const emit = defineEmits<{
  headerCTA: []
}>()

const handleHeaderCTA = () => {
  emit('headerCTA')
}
</script>

<style>
/* Глобальные стили для маркетингового лейаута */
html {
  scroll-behavior: auto;
  /* Убираем smooth для предотвращения автоскролла */
}

body {
  font-family: var(--font-halvar), 'Halvar Breitschrift', system-ui, -apple-system, sans-serif;
  background-color: var(--color-background);
  color: var(--color-foreground);
  overflow-x: hidden;
}

/* Применяем дизайн-токены */
:root {
  /* Цвета */
  --color-primary: #ff3f1a;
  --color-primary-foreground: #ffffff;
  --color-secondary: #cefaff;
  --color-accent: #ff6d51;
  --color-muted: rgba(236, 236, 236, 0.7);
  
  --color-success: #10b981;
  --color-warning: #f59e0b;
  --color-danger: #ef4444;
  
  --color-background: #161616;
  --color-surface: rgba(0, 0, 0, 0.1);
  --color-border: rgba(255, 255, 255, 0.25);
  --color-ring: rgba(255, 63, 26, 0.22);
  
  --color-foreground: #ececec;
  --color-foreground-muted: rgba(236, 236, 236, 0.7);
  
  /* Типографика */
  --font-gotham: 'Gotham', sans-serif;
  --font-halvar: 'Halvar Breitschrift', sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
  
  --text-xs: 0.75rem;
  --text-sm: 0.875rem;
  --text-base: 1rem;
  --text-lg: 1.125rem;
  --text-xl: 1.25rem;
  --text-2xl: 1.5rem;
  --text-3xl: 1.875rem;
  --text-4xl: 2.25rem;
  
  /* Отступы */
  --spacing-0-5: 0.125rem;
  --spacing-1: 0.25rem;
  --spacing-1-5: 0.375rem;
  --spacing-2: 0.5rem;
  --spacing-3: 0.75rem;
  --spacing-4: 1rem;
  --spacing-6: 1.5rem;
  --spacing-8: 2rem;
  --spacing-12: 3rem;
  --spacing-16: 4rem;
  
  /* Радиусы */
  --radius-sm: 0.25rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
  --radius-2xl: 1.5rem;
  
  /* Тени */
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  --shadow-inner: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
}

/* Утилитарные классы для шрифтов */
.font-heading {
  font-family: var(--font-halvar), var(--font-gotham), sans-serif;
}

.font-primary {
  font-family: var(--font-gotham), sans-serif;
}

.font-mono {
  font-family: var(--font-mono), monospace;
}
</style>




